// Quiz Bank buttons. Pure: data in, HTML out.
// Words: TRAINING_RECORD_WORDS.md (Quiz Bank page), TRAINING_SERVER_WORDS.md call 10.
// Mockup: MOCKUP_QUIZ_BANK_APPROVED.html (2026-10-03), screens 4 to 6.
// Never deletes. Every change goes through server code and logs who and when.

import { requiredCorrect, type QuizQuestion } from "./training";
import type { QuestionInput } from "./training-server";
import { esc } from "./training-screens";
import { QUESTION_TARGET, type QuizSummary } from "./training-view";

function sheet(title: string, body: string): string {
  return `<div class="shade"><div class="sheet" role="dialog" aria-modal="true" aria-label="${esc(title)}">
    <div class="big">${esc(title)}</div>${body}</div></div>`;
}

function stat(label: string, value: string): string {
  return `<div class="stat"><span>${esc(label)}</span><span>${esc(value)}</span></div>`;
}

function passText(pct: number, count: number): string {
  return count > 0 ? `${requiredCorrect(count, pct)} of ${count}` : "No active questions";
}

/** Switch a question off or on. Shows the new count and what it takes to pass. */
export function switchSheet(q: QuizQuestion, s: QuizSummary, busy: boolean, error = ""): string {
  const off = q.isActive;
  const from = s.activeCount;
  const to = off ? from - 1 : from + 1;
  const pct = s.quiz.passMarkPct;
  const lead = off
    ? `${s.quiz.quizName} drops to ${to} active ${to === 1 ? "question" : "questions"}${to < QUESTION_TARGET ? `, under the target of ${QUESTION_TARGET}` : ""}. The next trainee sees ${to}. Attempts already taken keep the questions they saw.`
    : `${s.quiz.quizName} goes to ${to} active ${to === 1 ? "question" : "questions"}${to > QUESTION_TARGET ? `, over the target of ${QUESTION_TARGET}` : ""}. The next trainee sees it.`;
  return sheet(
    `${off ? "Turn off" : "Turn on"} Question ${q.questionNumber}?`,
    `<p>${esc(lead)}</p>
    ${stat("Active", `${from} to ${to}`)}
    ${stat(`To pass at ${pct} percent`, `${passText(pct, from)} to ${passText(pct, to)}`)}
    ${error ? `<div class="note red">${esc(error)}</div>` : ""}
    <button class="go" type="button" data-act="qb-switch-confirm"${busy ? " disabled" : ""}>${busy ? "Saving…" : off ? "Turn Off" : "Turn On"}</button>
    <button class="go2" type="button" data-act="sheet-cancel">${off ? "Keep Active" : "Keep Off"}</button>`,
  );
}

/** Change the pass mark. Applies to the next attempt; old attempts keep theirs. */
export function passMarkSheet(s: QuizSummary, value: string, busy: boolean, error = ""): string {
  const from = s.quiz.passMarkPct;
  const n = Number(value);
  const ok = Number.isInteger(n) && n >= 1 && n <= 100;
  return sheet(
    `Change the ${s.quiz.quizName} pass mark?`,
    `<p>Applies to the next attempt. Attempts already taken keep the mark they were graded on. Logged with who and when.</p>
    <div class="sheet-card"><label class="fl"><span class="lbl">New pass mark, percent</span><input id="pm-input" type="number" min="1" max="100" step="1" inputmode="numeric" value="${esc(value)}"></label></div>
    <div id="pm-stats">${passMarkStats(s, value)}</div>
    ${error ? `<div class="note red">${esc(error)}</div>` : ""}
    <button class="go" type="button" data-act="pm-confirm"${busy || !ok || n === from ? " disabled" : ""}>${busy ? "Saving…" : "Save Pass Mark"}</button>
    <button class="go2" type="button" data-act="sheet-cancel">Cancel</button>`,
  );
}

/** The two lines under the pass mark field. Redrawn as the number changes. */
export function passMarkStats(s: QuizSummary, value: string): string {
  const from = s.quiz.passMarkPct;
  const n = Number(value);
  const ok = Number.isInteger(n) && n >= 1 && n <= 100;
  return `${stat("Pass mark", `${from} to ${ok ? n : "?"} percent`)}${stat("To pass", `${passText(from, s.activeCount)} to ${ok ? passText(n, s.activeCount) : "?"}`)}`;
}

// ---- Add or edit a question ----

export function entryOf(q: QuizQuestion): QuestionInput {
  return {
    questionText: q.questionText,
    optionA: q.optionA,
    optionB: q.optionB,
    optionC: q.optionC,
    optionD: q.optionD,
    correctAnswer: q.correctAnswer,
    slideRef: q.slideRef,
  };
}

const BLANK: QuestionInput = { questionText: "", optionA: "", optionB: "", optionC: "", optionD: "", correctAnswer: "", slideRef: "" };

/**
 * One form for Add Question and Change. A to D, or True and False (C and D blank).
 * Past attempts keep the text they saw, so an edit never changes an old result.
 */
export function questionForm(
  s: QuizSummary,
  q: QuizQuestion | null,
  entry: (QuestionInput & { type?: "MC" | "TF" }) | null,
  busy: boolean,
  error = "",
): string {
  const e = entry ?? (q ? entryOf(q) : BLANK);
  const type = entry?.type ?? (q ? q.questionType : "MC");
  const tf = type === "TF";
  const quizId = s.quiz.quizId;
  const next = Math.max(0, ...s.questions.map((x) => x.questionNumber)) + 1;
  const title = q ? `Change Question ${q.questionNumber}` : "Add Question";
  const back = q ? `#/quiz-bank/${quizId}/${esc(q.questionId)}` : `#/quiz-bank/${quizId}`;
  const opt = (k: "A" | "B" | "C" | "D") => {
    const v = e[`option${k}`];
    const ph = tf && k === "A" ? "True" : tf && k === "B" ? "False" : "Option text";
    return `<label class="r${k === "C" || k === "D" ? " qb-cd" : ""}"${tf && (k === "C" || k === "D") ? " hidden" : ""}><span class="k">Option ${k}</span><span class="v"><textarea class="fin wide" name="option${k}" rows="2" maxlength="400" placeholder="${ph}">${esc(v)}</textarea></span><span class="e"></span></label>`;
  };
  const correct = (["A", "B", "C", "D"] as const)
    .map(
      (k) =>
        `<label class="segk${k === "C" || k === "D" ? " qb-cd" : ""}"${tf && (k === "C" || k === "D") ? " hidden" : ""}><input type="radio" name="correctAnswer" value="${k}"${e.correctAnswer === k ? " checked" : ""}><span>${k}</span></label>`,
    )
    .join("");
  const note = q
    ? "Saved with your name and the time. Attempts already taken keep the text they saw."
    : `Becomes Question ${next}, Active. ${s.quiz.quizName} goes to ${s.activeCount + 1} of ${QUESTION_TARGET} target.`;
  return `<a class="back" href="${back}">Cancel</a>
    <div class="ident"><div class="badge">QB</div><div class="ident-text"><h1>${esc(title)}</h1><div class="sub">${esc(s.quiz.quizName)}${q ? ` · ${esc(q.questionId)}` : ""}</div></div></div>
    <form class="box" id="qb-form" data-quiz="${quizId}" data-q="${q ? esc(q.questionId) : ""}" novalidate>
      <div class="rows">
        <label class="r"><span class="k">Question</span><span class="v"><textarea class="fin wide" name="questionText" rows="4" maxlength="600" placeholder="Scenario, in plain words">${esc(e.questionText)}</textarea></span><span class="e"></span></label>
        <div class="r"><span class="k">Type</span><span class="v"><span class="seg2">
          <label class="segk wide"><input type="radio" name="qtype" value="MC"${tf ? "" : " checked"}><span>A to D</span></label>
          <label class="segk wide"><input type="radio" name="qtype" value="TF"${tf ? " checked" : ""}><span>True / False</span></label>
        </span></span><span class="e"></span></div>
        ${opt("A")}${opt("B")}${opt("C")}${opt("D")}
        <div class="r"><span class="k">Correct answer</span><span class="v"><span class="seg2">${correct}</span></span><span class="e"></span></div>
        <label class="r"><span class="k">Slide reference</span><span class="v"><input class="fin" name="slideRef" maxlength="40" value="${esc(e.slideRef)}" placeholder="for example 23"></span><span class="e"></span></label>
      </div>
    </form>
    ${error ? `<div class="note red">${esc(error)}</div>` : ""}
    <div class="note">${esc(note)}</div>
    <button class="prim" type="submit" form="qb-form"${busy ? " disabled" : ""}>${busy ? "Saving…" : "Save"}</button>`;
}

/** Edit history lines, newest first. */
export function historyHtml(q: QuizQuestion & { editHistory?: { action: string; byName?: string; by?: string; at: string }[] }): string {
  const h = [...(q.editHistory ?? [])].sort((a, b) => b.at.localeCompare(a.at));
  if (!h.length) return "Copied from the Quiz Bank review page";
  return `<span class="hist">${h
    .slice(0, 8)
    .map((x) => `<b>${esc(x.action[0]!.toUpperCase() + x.action.slice(1))}</b> by ${esc(x.byName ?? x.by ?? "")} · ${esc(new Date(x.at).toLocaleString("en-US", { timeZone: "America/New_York", month: "2-digit", day: "2-digit", year: "numeric", hour: "numeric", minute: "2-digit" }))}`)
    .join("<br>")}</span>`;
}
